import React, { useMemo, useState } from 'react';
import {
  DndContext, DragEndEvent, DragStartEvent, DragOverlay,
  pointerWithin, closestCenter, CollisionDetection,
  useSensor, useSensors, PointerSensor, KeyboardSensor,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '../../contexts/ThemeContext';
import { Task, TaskStatus, KANBAN_COLUMNS, ACCENT_CLASSES } from '../../types/work';
import { PriorityChip } from './PriorityChip';
import { DueDateLabel } from './DueDateLabel';

interface Props {
  tasks: Task[];
  onStatusChange: (taskId: string, newStatus: TaskStatus) => void;
  onTaskClick: (task: Task) => void;
}

// ── Collision detection: pointerWithin with closestCenter fallback ──
const kanbanCollision: CollisionDetection = (args) => {
  const p = pointerWithin(args);
  return p.length > 0 ? p : closestCenter(args);
};

// ── Sortable Task Card ───────────────────────────────────────────
const SortableTaskCard: React.FC<{ task: Task; onClick: () => void }> = ({ task, onClick }) => {
  const { darkMode } = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-shadow ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
          : 'bg-white border-gray-200 hover:border-gray-300'
      } ${isDragging ? 'shadow-xl ring-2 ring-violet-500/30' : ''} ${
        task.status === 'completed' ? 'opacity-60' : ''
      }`}
    >
      <p className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through text-gray-500' : darkMode ? 'text-white' : 'text-gray-900'}`}>
        {task.title}
      </p>
      <div className="flex items-center gap-2 mt-1.5">
        <PriorityChip priority={task.priority} />
        <DueDateLabel date={task.due_date} />
      </div>
    </div>
  );
};

// ── Kanban Column (with useDroppable for empty columns) ──────────
const KanbanColumn: React.FC<{
  column: typeof KANBAN_COLUMNS[number];
  tasks: Task[];
  onTaskClick: (task: Task) => void;
}> = ({ column, tasks, onTaskClick }) => {
  const { darkMode } = useTheme();
  const { setNodeRef } = useDroppable({ id: column.id }); // FIX-5: droppable on column
  const colors = ACCENT_CLASSES[column.accent];

  return (
    <div className={`flex flex-col min-w-[260px] max-w-[300px] w-full rounded-xl ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
      {/* Column header */}
      <div className={`px-3 py-2.5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${colors.fill}`} />
            <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{column.label}</span>
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
            {tasks.length}
          </span>
        </div>
      </div>

      {/* Cards — ref on container for droppable */}
      <div ref={setNodeRef} className="p-2 space-y-2 flex-1 min-h-[100px]">
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map(task => (
            <SortableTaskCard key={task.id} task={task} onClick={() => onTaskClick(task)} />
          ))}
        </SortableContext>
        {tasks.length === 0 && (
          <div className={`text-center py-8 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Drop tasks here
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main KanbanBoard ─────────────────────────────────────────────
export const KanbanBoard: React.FC<Props> = ({ tasks, onStatusChange, onTaskClick }) => {
  const { darkMode } = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);

  // FIX-7: Activation constraint to prevent click-vs-drag conflict
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // FIX-8: filter cancelled tasks
  const kanbanTasks = useMemo(() => tasks.filter(t => t.status !== 'cancelled'), [tasks]);

  const grouped = useMemo(() => {
    const map: Record<string, Task[]> = {};
    KANBAN_COLUMNS.forEach(col => { map[col.id] = []; });
    kanbanTasks.forEach(task => {
      if (map[task.status]) map[task.status].push(task);
      else map['todo'].push(task);
    });
    return map;
  }, [kanbanTasks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const task = kanbanTasks.find(t => t.id === taskId);
    if (!task) return;

    // Determine target: over could be a task or a column droppable
    const overTask = kanbanTasks.find(t => t.id === over.id);
    const targetStatus = overTask ? overTask.status : (over.id as TaskStatus);

    if (targetStatus !== task.status && KANBAN_COLUMNS.some(c => c.id === targetStatus)) {
      onStatusChange(taskId, targetStatus);
    }
  };

  const activeTask = activeId ? kanbanTasks.find(t => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={kanbanCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map(column => (
          <KanbanColumn
            key={column.id}
            column={column}
            tasks={grouped[column.id] || []}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
      <DragOverlay>
        {activeTask && (
          <div className={`p-3 rounded-lg shadow-2xl ring-2 ring-violet-500/20 max-w-[280px] ${darkMode ? 'bg-gray-800 border border-violet-500/30' : 'bg-white border border-violet-300'}`}>
            <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{activeTask.title}</p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
